import { useMutation, useQueryClient } from '@tanstack/react-query';

import { MutationKey, QueryKey } from '@/config/query-client';
import { useCompanies } from '@/features/companies/hooks/use-companies.query';
import type {
  CreateCompanyFromCBPayload,
  CreatedCompanyFromCB,
} from '@/api/v4/organization.api';
import { createCompany, createCompanyFromCB } from '@/api/v4/organization.api';

export const useCreateCompany = () => {
  const queryClient = useQueryClient();
  const { refetch: refreshCompanies } = useCompanies();

  const onCreateSuccess = async () => {
    await refreshCompanies();
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [QueryKey.CompaniesKpi] }),
      queryClient.invalidateQueries({ queryKey: [QueryKey.Companies] }),
      queryClient.invalidateQueries({
        queryKey: [QueryKey.CompanyOptionsCount],
      }),
      queryClient.invalidateQueries({
        queryKey: [QueryKey.CompaniesStatistics],
      }),
      queryClient.invalidateQueries({ queryKey: [QueryKey.LastActivities] }),
      queryClient.invalidateQueries({
        queryKey: [QueryKey.LastActivitiesCount],
      }),
    ]);
  };

  const createCompanyFromCrunchbaseMutation = useMutation({
    mutationFn: createCompanyFromCB,
    mutationKey: [MutationKey.CreateCompanyFromCrunchbase],
  });

  const createCompanyFromCrunchbase = (
    payload: CreateCompanyFromCBPayload,
    onSuccessHandler?: (data: CreatedCompanyFromCB) => void,
  ) => {
    return createCompanyFromCrunchbaseMutation.mutateAsync(payload, {
      onSuccess: async companyResponse => {
        onSuccessHandler?.(companyResponse);
        await onCreateSuccess();
      },
    });
  };

  const createOwnCompanyProfile = useMutation({
    mutationFn: createCompany,
    onSuccess: onCreateSuccess,
  });

  return {
    createCompanyFromCrunchbase,
    isCreatingCompanyFromCrunchbaseLoading:
      createCompanyFromCrunchbaseMutation.isPending,
    createOwnCompanyProfile,
  };
};
